<template>
  <div class="min-h-screen bg-base-100">
    <div class="container mx-auto px-4 max-w-2xl pt-4">
      <RouterLink :to="{ path: '/emailsetup', query: routeQuery }" class="btn btn-ghost btn-sm text-base-content/70 gap-1 -ml-2">
        {{ $t("emailConfig.tutorial.back") }}
      </RouterLink>
    </div>

    <!-- STICKY PROGRESS BAR -->
    <div class="sticky top-0 z-20 bg-base-100/95 backdrop-blur border-b border-base-200 shadow-sm">
      <div class="container mx-auto px-4 max-w-2xl py-3">
        <div class="flex items-center justify-between mb-1.5">
          <span class="text-sm font-semibold text-base-content">
            {{ $t("emailConfig.tutorial.progress", { current: currentStep, total: TOTAL_STEPS }) }}
          </span>
          <span class="text-xs text-base-content/60 truncate max-w-[60%] text-right">
            {{ steps[currentStep - 1].title }}
          </span>
        </div>
        <div
          role="progressbar"
          :aria-valuenow="progressPercent"
          aria-valuemin="0"
          aria-valuemax="100"
          class="w-full h-2.5 bg-base-200 rounded-full overflow-hidden">
          <div
            class="h-full bg-gradient-to-r from-primary to-secondary rounded-full transition-[width] duration-500 ease-out"
            :style="{ width: `${progressPercent}%` }" />
        </div>
      </div>
    </div>

    <!-- PAGE HEADER -->
    <div class="container mx-auto px-4 max-w-2xl pt-10 pb-6 text-center">
      <div
        class="inline-flex items-center gap-2 bg-primary/10 text-primary rounded-full px-4 py-1.5 text-sm font-medium mb-4">
        {{ $t("emailConfig.gmailTutorial.badge") }}
      </div>
      <h1 class="text-3xl md:text-4xl font-extrabold text-base-content mb-3">{{ $t("emailConfig.gmailTutorial.title") }}</h1>
      <p class="text-base-content/65 text-base max-w-lg mx-auto">
        {{ $t("emailConfig.gmailTutorial.subtitle", { steps: TOTAL_STEPS }) }}
      </p>

      <div v-if="emailAddress" class="mt-4 inline-flex items-center gap-2 bg-base-200 rounded-xl px-4 py-2 text-sm">
        <span class="text-base-content/60">{{ $t("emailConfig.tutorial.accountLabel") }}</span>
        <span class="font-mono font-semibold text-base-content">{{ emailAddress }}</span>
      </div>
    </div>

    <!-- TIMELINE -->
    <div class="container mx-auto px-4 max-w-2xl pb-16">
      <div class="relative">
        <div
          class="absolute left-[27px] top-8 bottom-8 w-0.5 bg-gradient-to-b from-primary/40 via-secondary/40 to-base-200"
          aria-hidden="true" />

        <div class="space-y-6">
          <div
            v-for="(step, index) in steps"
            :key="step.number"
            :ref="el => setStepRef(el as HTMLElement | null, index)"
            :id="`step-${step.number}`"
            class="relative flex gap-4 group">
            <div class="flex-shrink-0 flex flex-col items-center">
              <div
                :class="[
                  'w-14 h-14 rounded-2xl flex items-center justify-center text-lg font-extrabold shadow-md transition-all duration-300 z-10',
                  currentStep === step.number
                    ? 'bg-primary text-primary-content scale-110 ring-4 ring-primary/30'
                    : currentStep > step.number
                      ? 'bg-success text-success-content'
                      : 'bg-base-200 text-base-content/50',
                ]">
                <span v-if="currentStep > step.number">✓</span>
                <span v-else>{{ step.number }}</span>
              </div>
            </div>

            <div
              :class="[
                'flex-1 card bg-base-200 shadow transition-all duration-300 mb-2',
                currentStep === step.number ? 'ring-2 ring-primary/60 shadow-lg bg-base-100' : '',
              ]">
              <div class="card-body p-4 md:p-5">
                <h2 class="text-base font-bold text-base-content leading-snug">
                  {{ step.title }}
                </h2>

                <p class="text-sm text-base-content/70 mt-1">
                  {{ typeof step.description === "function" ? step.description() : step.description }}
                </p>

                <div class="mt-4 rounded-xl overflow-hidden border border-base-300 bg-base-300">
                  <img
                    :src="step.image"
                    :alt="$t('emailConfig.tutorial.stepImageAlt', { step: step.number, title: step.title })"
                    class="w-full max-h-[420px] object-contain object-top"
                    loading="lazy" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- FINAL CTA -->
    <div class="bg-gradient-to-br from-primary/10 to-secondary/10 border-t border-base-200">
      <div class="container mx-auto px-4 max-w-2xl py-14 text-center">
        <div class="text-4xl mb-4">🎉</div>
        <h2 class="text-2xl font-extrabold text-base-content mb-3">{{ $t("emailConfig.tutorial.successTitle") }}</h2>
        <p class="text-base-content/65 mb-2 max-w-md mx-auto">
          {{ $t("emailConfig.gmailTutorial.successDescription") }}
        </p>

        <div class="divider my-8 max-w-sm mx-auto">{{ $t("emailConfig.tutorial.helpDivider") }}</div>

        <div class="card bg-base-100 shadow-xl max-w-sm mx-auto">
          <div class="card-body text-center">
            <div class="text-3xl mb-2">🛠️</div>
            <h3 class="font-bold text-lg text-base-content mb-1">{{ $t("emailConfig.tutorial.remoteTitle") }}</h3>
            <p class="text-sm text-base-content/65 mb-4">
              {{ $t("emailConfig.tutorial.remoteDescription") }}
            </p>
            <a
              href="https://cal.com/siteoplossing/remote-email-installation"
              target="_blank"
              rel="noopener"
              class="btn btn-primary w-full">
              {{ $t("emailConfig.tutorial.remoteCta") }}
            </a>
            <p class="text-xs text-base-content/40 mt-2">{{ $t("emailConfig.tutorial.remoteNote") }}</p>
          </div>
        </div>

        <div class="mt-8">
          <RouterLink :to="{ path: '/emailsetup', query: routeQuery }" class="btn btn-ghost btn-sm text-base-content/50">
            {{ $t("emailConfig.tutorial.back") }}
          </RouterLink>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";
import { RouterLink } from "vue-router";
import { useI18n } from "vue-i18n";
import { getMailHost } from "@/utils/emailHosts";
import { useEmailSetupRoute } from "@/composables/useEmailSetupRoute";

const { t } = useI18n();

const emailAddress = ref<string>("");
const { routeQuery } = useEmailSetupRoute({ emailRef: emailAddress });
const mailDomain = computed(() => getMailHost(emailAddress.value));

const TOTAL_STEPS = 9;
const IMG_BASE = "/images/tutorials/gmail";

const stepImages: Record<number, string> = {
  1: `${IMG_BASE}/1-open%20gmail.png`,
  2: `${IMG_BASE}/2-enter%20your%20mail%20and%20click%20on%20next.png`,
  3: `${IMG_BASE}/3-press%20on%20'personal%20(IMAP).png`,
  4: `${IMG_BASE}/4-Fill%20in%20password%20and%20click%20on%20next.png`,
  5: `${IMG_BASE}/6-ensure%20server%20input%20states%20'mail.yourdomain.com'%20and%20click%20on%20next.png`,
  6: `${IMG_BASE}/7-input%20is%20being%20verified%20if%20you%20pass%20this%20these%20settings%20have%20been%20configured%20correctly.png`,
  7: `${IMG_BASE}/8-Now%20we%20do%20Outgoing%20and%20ensure%20that%20smtp%20server%20is%20stated%20as%20mail.yourdomain.com%20and%20click%20on%20next.png`,
  8: `${IMG_BASE}/9-standard%20configuration%20can%20be%20kept%20and%20continue%20with%20next.png`,
  9: `${IMG_BASE}/10-change%20account%20name%20and%20setup%20your%20name%20and%20click%20on%20next%20and%20youre%20done.png`,
};

const steps = computed(() =>
  Array.from({ length: TOTAL_STEPS }, (_, i) => {
    const number = i + 1;
    const key = `emailConfig.gmailTutorial.steps.${number}`;
    const needsMailServer = number === 5 || number === 7;
    return {
      number,
      title: t(`${key}.title`),
      description: needsMailServer
        ? () => t(`${key}.description`, { mailServer: mailDomain.value })
        : t(`${key}.description`),
      image: stepImages[number],
    };
  }),
);

const currentStep = ref(1);
const stepRefs = ref<(HTMLElement | null)[]>(Array(TOTAL_STEPS).fill(null));

const setStepRef = (el: HTMLElement | null, index: number) => {
  stepRefs.value[index] = el;
};

const progressPercent = computed(() => Math.round((currentStep.value / TOTAL_STEPS) * 100));

let observer: IntersectionObserver | null = null;

onMounted(() => {
  observer = new IntersectionObserver(
    entries => {
      let bestStep = currentStep.value;
      let bestRatio = 0;

      entries.forEach(entry => {
        if (entry.isIntersecting && entry.intersectionRatio >= bestRatio) {
          const stepId = (entry.target as HTMLElement).getAttribute("id");
          if (stepId) {
            const num = parseInt(stepId.replace("step-", ""), 10);
            if (!isNaN(num)) {
              bestRatio = entry.intersectionRatio;
              bestStep = num;
            }
          }
        }
      });

      currentStep.value = bestStep;
    },
    {
      rootMargin: "-10% 0px -60% 0px",
      threshold: [0, 0.1, 0.5, 1.0],
    },
  );

  stepRefs.value.forEach(el => {
    if (el) observer!.observe(el);
  });
});

onUnmounted(() => {
  observer?.disconnect();
});
</script>
